import { z } from 'zod';
import { LIMITS } from './constants.ts';

/**
 * Six distinct temporary character slots (shape + colour). Player identity is the participant ID;
 * the slot is presentation data that maps to an asset slot for later Blender exports.
 * The black receiver robot is a scene asset, not a player slot.
 */
export const CHARACTER_SLOT_IDS = [
  'lilac-square',
  'blue-oval',
  'coral-triangle',
  'yellow-hexagon',
  'green-diamond',
  'charcoal-circle',
] as const;
export const CharacterSlotId = z.enum(CHARACTER_SLOT_IDS);
export type CharacterSlotId = z.infer<typeof CharacterSlotId>;

export const CharacterShape = z.enum(['square', 'oval', 'triangle', 'hexagon', 'diamond', 'circle']);
export type CharacterShape = z.infer<typeof CharacterShape>;

export interface CharacterAppearance {
  slot: CharacterSlotId;
  shape: CharacterShape;
  /** Design-token name, resolved by the client theme (e.g. `--char-lilac`). */
  colorToken: string;
  label: string;
  /** Asset slot key. Renderers look up `assets/characters/<assetSlot>` (SVG placeholder now, model later). */
  assetSlot: string;
}

export const CHARACTER_SLOTS: Record<CharacterSlotId, CharacterAppearance> = {
  'lilac-square': { slot: 'lilac-square', shape: 'square', colorToken: 'char-lilac', label: 'Lilac square', assetSlot: 'character-square' },
  'blue-oval': { slot: 'blue-oval', shape: 'oval', colorToken: 'char-blue', label: 'Blue oval', assetSlot: 'character-oval' },
  'coral-triangle': { slot: 'coral-triangle', shape: 'triangle', colorToken: 'char-coral', label: 'Coral triangle', assetSlot: 'character-triangle' },
  'yellow-hexagon': { slot: 'yellow-hexagon', shape: 'hexagon', colorToken: 'char-yellow', label: 'Yellow hexagon', assetSlot: 'character-hexagon' },
  'green-diamond': { slot: 'green-diamond', shape: 'diamond', colorToken: 'char-green', label: 'Green diamond', assetSlot: 'character-diamond' },
  'charcoal-circle': { slot: 'charcoal-circle', shape: 'circle', colorToken: 'char-charcoal', label: 'Charcoal circle', assetSlot: 'character-circle' },
};

/** The customer/receiver robot asset slot (scene actor, never a participant). */
export const RECEIVER_ASSET_SLOT = 'receiver';

/**
 * `player`: active participant. `spectator`: read-only display. `presenter`: operates a presenter session,
 * may act as simulated participants and switch perspectives. Host is a flag, not a role: a host may play
 * (counts once as a player) or facilitate without playing.
 */
export const ParticipantRole = z.enum(['player', 'spectator', 'presenter']);
export type ParticipantRole = z.infer<typeof ParticipantRole>;

export const ConnectionStatus = z.enum(['connected', 'disconnected', 'left', 'removed']);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

export const DisplayName = z
  .string()
  .trim()
  .min(1)
  .max(LIMITS.DISPLAY_NAME_MAX);

/** What every client may know about a participant. Never includes credentials. */
export const ParticipantPublic = z.object({
  id: z.string(),
  displayName: DisplayName,
  role: ParticipantRole,
  isHost: z.boolean(),
  characterSlot: CharacterSlotId.nullable(),
  teamId: z.string().nullable(),
  connection: ConnectionStatus,
  disconnectedAt: z.number().nullable(),
  ready: z.boolean(),
  /** Self-reported mute flag for the indicator; enforcement lives in the voice client. */
  muted: z.boolean(),
  /** Presenter-operated simulated participant. Always rendered with an explicit label. */
  simulated: z.boolean(),
  joinedAt: z.number(),
});
export type ParticipantPublic = z.infer<typeof ParticipantPublic>;

/** The connected client's own identity as confirmed by the server. */
export const SelfInfo = z.object({
  participantId: z.string(),
  role: ParticipantRole,
  isHost: z.boolean(),
  teamId: z.string().nullable(),
  simulated: z.boolean(),
});
export type SelfInfo = z.infer<typeof SelfInfo>;
